import React, { useState } from 'react';
import { 
  Image, 
  MapPin, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Clock,
  User,
  Store,
  Truck,
  ChevronLeft,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface DisputeEvidence {
  id: string;
  type: 'photo' | 'gps' | 'chat' | 'verification';
  title: string;
  description?: string;
  data: any; // Varies by type
  timestamp: Date;
}

interface DisputeEvidenceViewerProps {
  disputeId: string;
  evidence: DisputeEvidence[];
  onRefund: () => void;
  onPartialRefund: () => void;
  onDeny: () => void;
}

export function DisputeEvidenceViewer({ 
  disputeId, 
  evidence, 
  onRefund, 
  onPartialRefund, 
  onDeny 
}: DisputeEvidenceViewerProps) {
  const [selectedEvidence, setSelectedEvidence] = useState<number>(0);
  const currentEvidence = evidence[selectedEvidence];

  const renderEvidenceContent = (item: DisputeEvidence) => {
    switch (item.type) {
      case 'photo':
        return (
          <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden">
            <img
              src={item.data.url}
              alt={item.title}
              className="w-full h-full object-contain"
            />
            {item.data.metadata && (
              <div className="absolute bottom-4 left-4 right-4 bg-black/70 text-white text-xs p-3 rounded-lg">
                <div className="grid grid-cols-2 gap-2">
                  <span>Taken: {new Date(item.data.metadata.capturedAt).toLocaleString()}</span>
                  {item.data.metadata.location && (
                    <span>Location: {item.data.metadata.location}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'gps':
        return (
          <div className="space-y-4">
            {/* Map placeholder */}
            <div className="aspect-video bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-12 w-12 text-primary mx-auto mb-2" />
                <p className="text-heading font-medium">GPS Trail Available</p>
                <Button variant="outline" size="sm" className="mt-3">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Full Map
                </Button>
              </div>
            </div>
            
            {/* GPS Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-body">Pickup Location</p>
                <p className="font-medium text-heading">{item.data.pickup}</p>
                <p className="text-xs text-gray-400">{item.data.pickupTime}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-body">Dropoff Location</p>
                <p className="font-medium text-heading">{item.data.dropoff}</p>
                <p className="text-xs text-gray-400">{item.data.dropoffTime}</p>
              </div>
            </div>
            
            {/* Distance from expected */}
            {item.data.distanceFromExpected && (
              <div className={cn(
                "p-3 rounded-lg text-sm",
                item.data.distanceFromExpected > 100 
                  ? "bg-error/10 text-error" 
                  : "bg-success/10 text-success"
              )}>
                Dropoff was {item.data.distanceFromExpected}m from expected location
              </div>
            )}
          </div>
        );
      
      case 'chat':
        return (
          <div className="bg-gray-50 rounded-xl p-4 max-h-96 overflow-y-auto space-y-3">
            {item.data.messages.map((msg: any, idx: number) => (
              <div 
                key={idx}
                className={cn(
                  "flex",
                  msg.sender === 'customer' ? "justify-end" : "justify-start"
                )}
              >
                <div className={cn(
                  "max-w-[80%] rounded-2xl px-4 py-2",
                  msg.sender === 'customer' 
                    ? "bg-primary text-white rounded-br-sm" 
                    : "bg-white text-heading rounded-bl-sm shadow-sm"
                )}>
                  <p className="text-sm">{msg.text}</p>
                  <p className={cn(
                    "text-xs mt-1",
                    msg.sender === 'customer' ? "text-white/60" : "text-gray-400"
                  )}>
                    {new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        );
      
      case 'verification':
        return (
          <div className="space-y-4">
            <div className={cn(
              "p-6 rounded-xl text-center",
              item.data.verified ? "bg-success/10" : "bg-error/10"
            )}>
              {item.data.verified ? (
                <CheckCircle className="h-12 w-12 text-success mx-auto mb-3" />
              ) : (
                <XCircle className="h-12 w-12 text-error mx-auto mb-3" />
              )}
              <p className="font-medium text-heading text-lg">
                {item.data.verified ? 'Verification Successful' : 'Verification Failed'}
              </p>
              <p className="text-body mt-1">{item.description}</p>
            </div>
            
            {item.data.attempts && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-heading mb-2">Verification Attempts</p>
                {item.data.attempts.map((attempt: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between text-sm py-1">
                    <span className="text-body">Attempt {idx + 1}</span>
                    <div className="flex items-center space-x-2">
                      <span className={attempt.success ? "text-success" : "text-error"}>
                        {attempt.success ? 'Success' : 'Failed'}
                      </span>
                      <span className="text-gray-400">{attempt.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      
      default:
        return <p className="text-body">No preview available</p>;
    }
  };

  const typeIcons = {
    photo: Image,
    gps: MapPin,
    chat: MessageSquare,
    verification: CheckCircle,
  };

  return (
    <div className="space-y-6">
      {/* Evidence Tabs */}
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {evidence.map((item, idx) => {
          const Icon = typeIcons[item.type];
          return (
            <button
              key={item.id}
              onClick={() => setSelectedEvidence(idx)}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all",
                selectedEvidence === idx
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-body hover:bg-gray-200"
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{item.title}</span>
            </button>
          );
        })}
      </div>

      {/* Evidence Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              {React.createElement(typeIcons[currentEvidence.type], { className: "h-5 w-5 text-body" })}
              <span>{currentEvidence.title}</span>
            </CardTitle>
            <div className="flex items-center space-x-2 text-sm text-body">
              <Clock className="h-4 w-4" />
              <span>{new Date(currentEvidence.timestamp).toLocaleString()}</span>
            </div>
          </div>
          {currentEvidence.description && (
            <p className="text-sm text-body mt-1">{currentEvidence.description}</p>
          )}
        </CardHeader>
        <CardContent>
          {renderEvidenceContent(currentEvidence)}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setSelectedEvidence(prev => Math.max(0, prev - 1))}
          disabled={selectedEvidence === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <span className="text-sm text-body">
          {selectedEvidence + 1} of {evidence.length}
        </span>
        <Button
          variant="outline"
          onClick={() => setSelectedEvidence(prev => Math.min(evidence.length - 1, prev + 1))}
          disabled={selectedEvidence === evidence.length - 1}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Actions */}
      <Card className="bg-gray-50">
        <CardContent className="py-4">
          <h4 className="font-medium text-heading mb-4">Resolve Dispute</h4>
          <div className="flex flex-wrap gap-3">
            <Button onClick={onRefund} className="bg-success hover:bg-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              Full Refund
            </Button>
            <Button onClick={onPartialRefund} variant="outline">
              Partial Refund
            </Button>
            <Button onClick={onDeny} variant="outline" className="text-error hover:bg-error/10 hover:border-error">
              <XCircle className="h-4 w-4 mr-2" />
              Deny Claim
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


